import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import StarIcon from '@mui/icons-material/Star';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import type { AdminPortal } from '../../types/admin';
import { portalsApi } from '../../api/adminApi';
import { getApiErrorMessage, resolveApiAssetUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import { ImageUploader } from '../../components/ImageUploader';
import { usePortalBranding } from '../../context/PortalContext';
import {
  DEFAULT_LOGO_HEIGHT,
  LOGO_HEIGHT_MAX,
  LOGO_HEIGHT_MIN,
} from '../../components/portalLogoSize';
import { ConfirmDialog } from './ConfirmDialog';
import { PortalProjectsDialog } from './PortalProjectsDialog';

interface PortalFormState {
  open: boolean;
  mode: 'create' | 'edit';
  portal: AdminPortal | null;
  hostname: string;
  displayName: string;
  isActive: boolean;
  /** Mã hex người dùng đang gõ — rỗng nghĩa là dùng màu mặc định. */
  brandColor: string;
  logoOnDark: boolean;
  logoHeight: number;
  saving: boolean;
}

const EMPTY_FORM: PortalFormState = {
  open: false,
  mode: 'create',
  portal: null,
  hostname: '',
  displayName: '',
  isActive: true,
  brandColor: '',
  logoOnDark: false,
  logoHeight: DEFAULT_LOGO_HEIGHT,
  saving: false,
};

/** Màu mặc định hiển thị trong ô chọn khi cổng chưa khai báo màu riêng. */
const DEFAULT_SWATCH = '#1565c0';

/** Khớp cùng quy tắc với API (normalizeBrandColor) để báo lỗi ngay tại form. */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function PortalManagement(): JSX.Element {
  const { branding } = usePortalBranding();
  const [portals, setPortals] = useState<AdminPortal[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<PortalFormState>(EMPTY_FORM);
  const [logoTarget, setLogoTarget] = useState<AdminPortal | null>(null);
  const [logoFiles, setLogoFiles] = useState<File[]>([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [faviconTarget, setFaviconTarget] = useState<AdminPortal | null>(null);
  const [faviconFiles, setFaviconFiles] = useState<File[]>([]);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [projectsTarget, setProjectsTarget] = useState<AdminPortal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminPortal | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await portalsApi.list({ limit: rowsPerPage, offset: page * rowsPerPage });
      setPortals(result.items);
      setTotal(result.total);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách cổng truy cập'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => setForm({ ...EMPTY_FORM, open: true, mode: 'create' });

  const openEdit = (portal: AdminPortal) => {
    setForm({
      open: true,
      mode: 'edit',
      portal,
      hostname: portal.hostname,
      displayName: portal.display_name,
      isActive: portal.is_active === 1,
      brandColor: portal.brand_color,
      logoOnDark: portal.logo_on_dark === 1,
      logoHeight: portal.logo_height,
      saving: false,
    });
  };

  const closeForm = () => setForm(EMPTY_FORM);

  const handleSubmitForm = async () => {
    if (!form.hostname.trim() || !form.displayName.trim()) {
      notify('Vui lòng nhập đầy đủ hostname và tên hiển thị', 'warning');
      return;
    }
    const brandColor = form.brandColor.trim();
    if (brandColor && !HEX_RE.test(brandColor)) {
      notify('Mã màu phải có dạng #rrggbb (vd: #e3002b)', 'warning');
      return;
    }
    setForm((prev) => ({ ...prev, saving: true }));
    try {
      if (form.mode === 'create') {
        await portalsApi.create({
          hostname: form.hostname.trim(),
          display_name: form.displayName.trim(),
          brand_color: brandColor,
          logo_on_dark: form.logoOnDark,
          logo_height: form.logoHeight,
        });
        notify('Đã tạo cổng truy cập mới', 'success');
      } else if (form.portal) {
        await portalsApi.update(form.portal.id, {
          hostname: form.hostname.trim(),
          display_name: form.displayName.trim(),
          is_active: form.isActive,
          brand_color: brandColor,
          logo_on_dark: form.logoOnDark,
          logo_height: form.logoHeight,
        });
        notify('Đã cập nhật cổng truy cập', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu cổng truy cập'), 'error');
      setForm((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await portalsApi.remove(deleteTarget.id);
      notify('Đã xóa cổng truy cập', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa cổng truy cập'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const openLogoDialog = (portal: AdminPortal) => {
    setLogoTarget(portal);
    setLogoFiles([]);
  };

  const closeLogoDialog = () => {
    setLogoTarget(null);
    setLogoFiles([]);
  };

  const handleUploadLogo = async (files: File[]) => {
    if (!logoTarget || files.length === 0) return;
    setUploadingLogo(true);
    try {
      await portalsApi.uploadLogo(logoTarget.id, files[0]);
      notify('Đã cập nhật logo', 'success');
      closeLogoDialog();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải logo'), 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!logoTarget) return;
    setUploadingLogo(true);
    try {
      await portalsApi.removeLogo(logoTarget.id);
      notify('Đã gỡ logo', 'success');
      closeLogoDialog();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể gỡ logo'), 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const openFaviconDialog = (portal: AdminPortal) => {
    setFaviconTarget(portal);
    setFaviconFiles([]);
  };

  const closeFaviconDialog = () => {
    setFaviconTarget(null);
    setFaviconFiles([]);
  };

  const handleUploadFavicon = async (files: File[]) => {
    if (!faviconTarget || files.length === 0) return;
    setUploadingFavicon(true);
    try {
      await portalsApi.uploadFavicon(faviconTarget.id, files[0]);
      notify('Đã cập nhật favicon', 'success');
      closeFaviconDialog();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải favicon'), 'error');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleRemoveFavicon = async () => {
    if (!faviconTarget) return;
    setUploadingFavicon(true);
    try {
      await portalsApi.removeFavicon(faviconTarget.id);
      notify('Đã gỡ favicon — favicon sẽ tự cắt tròn từ logo', 'success');
      closeFaviconDialog();
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể gỡ favicon'), 'error');
    } finally {
      setUploadingFavicon(false);
    }
  };

  return (
    <Box>
      {branding !== null && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Bạn đang truy cập từ một cổng riêng — danh sách dự án có thể không đầy đủ. Vui lòng quản
          trị cổng từ domain gốc.
        </Alert>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Cổng truy cập theo domain
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Thêm cổng
        </Button>
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Logo</TableCell>
                <TableCell>Hostname</TableCell>
                <TableCell>Tên hiển thị</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell align="right">Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && portals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Chưa có cổng truy cập nào.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                portals.map((portal) => (
                  <TableRow key={portal.id} hover>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={resolveApiAssetUrl(portal.logo_url) ?? undefined}
                        sx={{
                          width: 32,
                          height: 32,
                          // Lót nền thương hiệu để logo chữ sáng không chìm vào
                          // ô xám mặc định của Avatar — khớp với trang đăng nhập.
                          bgcolor: portal.brand_color || undefined,
                          '& img': portal.logo_on_dark === 1 ? { objectFit: 'contain', p: 0.5 } : undefined,
                        }}
                      >
                        {portal.display_name.charAt(0).toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>{portal.hostname}</TableCell>
                    <TableCell>{portal.display_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={portal.is_active === 1 ? 'Hoạt động' : 'Đã tắt'}
                        size="small"
                        color={portal.is_active === 1 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Dự án thuộc cổng">
                        <IconButton
                          size="small"
                          aria-label={`Dự án thuộc cổng ${portal.display_name}`}
                          onClick={() => setProjectsTarget(portal)}
                        >
                          <FolderSharedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Logo">
                        <IconButton
                          size="small"
                          aria-label={`Logo cổng ${portal.display_name}`}
                          onClick={() => openLogoDialog(portal)}
                        >
                          <ImageIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Favicon riêng (tab trình duyệt)">
                        <IconButton
                          size="small"
                          aria-label={`Favicon cổng ${portal.display_name}`}
                          onClick={() => openFaviconDialog(portal)}
                        >
                          <StarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sửa">
                        <IconButton
                          size="small"
                          aria-label={`Sửa cổng ${portal.display_name}`}
                          onClick={() => openEdit(portal)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <IconButton
                          size="small"
                          aria-label={`Xóa cổng ${portal.display_name}`}
                          onClick={() => setDeleteTarget(portal)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Số dòng/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      <Dialog open={form.open} onClose={form.saving ? undefined : closeForm} maxWidth="xs" fullWidth>
        <DialogTitle>{form.mode === 'create' ? 'Thêm cổng truy cập' : 'Sửa cổng truy cập'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Hostname"
              placeholder="nghiemthu.khachabc.com"
              value={form.hostname}
              onChange={(e) => setForm((prev) => ({ ...prev, hostname: e.target.value }))}
              fullWidth
              required
              disabled={form.saving}
            />
            <TextField
              label="Tên hiển thị"
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
              fullWidth
              required
              disabled={form.saving}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Màu thương hiệu
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="input"
                  type="color"
                  aria-label="Chọn màu thương hiệu"
                  value={form.brandColor || DEFAULT_SWATCH}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((prev) => ({ ...prev, brandColor: e.target.value }))
                  }
                  disabled={form.saving}
                  sx={{
                    width: 56,
                    height: 40,
                    p: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                  }}
                />
                <TextField
                  value={form.brandColor}
                  onChange={(e) => setForm((prev) => ({ ...prev, brandColor: e.target.value }))}
                  placeholder={`${DEFAULT_SWATCH} (mặc định)`}
                  size="small"
                  fullWidth
                  disabled={form.saving}
                  error={Boolean(form.brandColor.trim()) && !HEX_RE.test(form.brandColor.trim())}
                  helperText="Để trống để dùng màu mặc định của hệ thống."
                />
              </Stack>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form.logoOnDark}
                  onChange={(e) => setForm((prev) => ({ ...prev, logoOnDark: e.target.checked }))}
                  disabled={form.saving}
                />
              }
              label="Logo cần nền tối"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Bật nếu logo có chữ/hình màu sáng — hệ thống sẽ lót nền màu thương hiệu phía sau để
              logo không bị chìm vào nền trắng của trang đăng nhập.
            </Typography>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Cỡ logo trang đăng nhập</Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.logoHeight} px
                </Typography>
              </Stack>
              <Slider
                value={form.logoHeight}
                onChange={(_, value) =>
                  setForm((prev) => ({ ...prev, logoHeight: Array.isArray(value) ? value[0] : value }))
                }
                min={LOGO_HEIGHT_MIN}
                max={LOGO_HEIGHT_MAX}
                step={4}
                marks={[
                  { value: LOGO_HEIGHT_MIN, label: `${LOGO_HEIGHT_MIN}` },
                  { value: DEFAULT_LOGO_HEIGHT, label: 'mặc định' },
                  { value: LOGO_HEIGHT_MAX, label: `${LOGO_HEIGHT_MAX}` },
                ]}
                disabled={form.saving}
                aria-label="Cỡ logo trang đăng nhập"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Logo trên thanh điều hướng tự co theo (tối đa 40px) để không làm vỡ thanh.
              </Typography>
            </Box>

            {form.mode === 'edit' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={form.saving}
                  />
                }
                label="Hoạt động"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={form.saving}>
            Hủy
          </Button>
          <Button
            onClick={() => void handleSubmitForm()}
            variant="contained"
            disabled={form.saving}
            startIcon={form.saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {form.mode === 'create' ? 'Tạo mới' : 'Lưu thay đổi'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logoTarget !== null} onClose={uploadingLogo ? undefined : closeLogoDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Logo: {logoTarget?.display_name}</DialogTitle>
        <DialogContent>
          {logoTarget?.logo_url && (
            <Box
              component="img"
              src={resolveApiAssetUrl(logoTarget.logo_url) ?? undefined}
              alt={logoTarget.display_name}
              sx={{ height: 48, mb: 2, display: 'block' }}
            />
          )}
          <ImageUploader value={logoFiles} onChange={setLogoFiles} multiple={false} disabled={uploadingLogo} />
        </DialogContent>
        <DialogActions>
          {logoTarget?.logo_url && (
            <Button color="error" onClick={() => void handleRemoveLogo()} disabled={uploadingLogo}>
              Gỡ logo
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={closeLogoDialog} disabled={uploadingLogo}>
            Hủy
          </Button>
          <Button
            onClick={() => void handleUploadLogo(logoFiles)}
            variant="contained"
            disabled={uploadingLogo || logoFiles.length === 0}
            startIcon={uploadingLogo ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Lưu logo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={faviconTarget !== null}
        onClose={uploadingFavicon ? undefined : closeFaviconDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Favicon riêng: {faviconTarget?.display_name}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Hiện trên tab trình duyệt. Nên dùng ảnh vuông — không bị cắt tròn tự động như logo. Chưa
            tải ảnh riêng thì web tự cắt tròn logo thương hiệu để dùng tạm.
          </Typography>
          {faviconTarget?.favicon_url && (
            <Box
              component="img"
              src={resolveApiAssetUrl(faviconTarget.favicon_url) ?? undefined}
              alt={faviconTarget.display_name}
              sx={{ width: 48, height: 48, mb: 2, display: 'block', borderRadius: '50%' }}
            />
          )}
          <ImageUploader value={faviconFiles} onChange={setFaviconFiles} multiple={false} disabled={uploadingFavicon} />
        </DialogContent>
        <DialogActions>
          {faviconTarget?.favicon_url && (
            <Button color="error" onClick={() => void handleRemoveFavicon()} disabled={uploadingFavicon}>
              Gỡ favicon
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={closeFaviconDialog} disabled={uploadingFavicon}>
            Hủy
          </Button>
          <Button
            onClick={() => void handleUploadFavicon(faviconFiles)}
            variant="contained"
            disabled={uploadingFavicon || faviconFiles.length === 0}
            startIcon={uploadingFavicon ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Lưu favicon
          </Button>
        </DialogActions>
      </Dialog>

      <PortalProjectsDialog
        open={projectsTarget !== null}
        portal={projectsTarget}
        onClose={() => setProjectsTarget(null)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa cổng truy cập"
        message={`Cổng "${deleteTarget?.display_name}" sẽ bị xóa. Người truy cập domain này sẽ không còn thấy các dự án đã gán. Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
